import React from 'react';
import { Typography } from '@mui/material';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { IClient } from '../../types/my-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface ClientDeleteDialogProps {
  open: boolean;
  client: IClient;
  onClose: () => void;
}

function ClientDeleteDialog({ open, client, onClose }: ClientDeleteDialogProps) {
  const { setToast } = React.useContext(AppContext);
  const glynt = useGlynt();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => (await glynt).clientApi.del(client.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['datapools'] });
      setToast({ variant: 'success', open: true, message: `Client "${client.name}" deleted.` });
      onClose();
    },
    onError: () => {
      setToast({ variant: 'error', open: true, message: 'Failed to delete client.' });
    },
  });

  return (
    <DialogGlynt
      open={open}
      closeModal={onClose}
      width="420px"
      cypressId="dialog-delete-client"
      loading={deleteMutation.isPending}
    >
      <DialogTitleGlynt
        title="Delete Client"
        closeModal={onClose}
        loading={deleteMutation.isPending}
      />
      <DialogContentGlynt>
        <Typography sx={{ fontSize: '14px', color: '#1a2332' }}>
          Delete client <strong>{client.name}</strong>? This will unassign it from all data pools.
        </Typography>
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={() => deleteMutation.mutate()}
        closeModal={onClose}
        cancelCypressId="btn-delete-client-cancel"
        submitCypressId="btn-delete-client-confirm"
        clearCypressId="btn-delete-client-clear"
        submitButtonText="Delete"
        submitLoading={deleteMutation.isPending}
      />
    </DialogGlynt>
  );
}

export default ClientDeleteDialog;
